import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import {
  useMediaStore,
  MediaFile,
  MediaType,
  getMediaType,
  isDesignExport,
  isAIGenerated,
  isStockPhoto,
  CropData,
  FormatCategoryType,
} from '../store/media';
import { useProjectsStore } from '../store/projects';
import { usePostsStore, Post } from '../store/posts';
import { useFlightsStore } from '../store/flights';
import { useCreatorStore, SavedDesign, DesignStatus } from '../store/creator';
import { MediaUpload } from '../components/MediaUpload';
import { CropEditorModal } from '../components/ImageCropEditor';
import { FormatCategory } from '../config/formatSpecs';
import { StockPhotoSearch } from '../components/StockPhotoSearch';
import { getMediaUrl } from '../services/api';

type SortField = 'created_at' | 'original_name' | 'size' | 'mime_type';
type SortOrder = 'asc' | 'desc';
type GroupBy = 'none' | 'type' | 'date' | 'source' | 'format' | 'smart';
type TypeFilter = 'all' | MediaType;
type FormatFilter = 'all' | FormatCategoryType;
type SourceFilter = 'all' | 'upload' | 'design' | 'ai' | 'stock';
type MediaTab = 'library' | 'stock' | 'designs';
type DesignStatusFilter = 'all' | DesignStatus;

export function MediaPage() {
  const { t } = useTranslation('media');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { media, isLoading, isCropping, error, fetchMedia, deleteMedia, cropMedia, batchAssignToFlight, saveDesignToLibrary, clearError } = useMediaStore();
  const { projects, selectedProjectId, fetchProjects, setSelectedProjectId } = useProjectsStore();
  const { posts, fetchPosts, updatePost, createPost } = usePostsStore();
  const { flights, fetchFlights } = useFlightsStore();
  const { savedDesigns, fetchSavedDesigns, updateDesign, isLoading: isLoadingDesigns } = useCreatorStore();
  const [filter, setFilter] = useState<'all' | 'unassigned' | 'assigned'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedMedia, setSelectedMedia] = useState<Set<string>>(new Set());
  const [previewMedia, setPreviewMedia] = useState<MediaFile | null>(null);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [mediaToAssign, setMediaToAssign] = useState<MediaFile | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [mediaToCrop, setMediaToCrop] = useState<MediaFile | null>(null);
  const [postSearchQuery, setPostSearchQuery] = useState('');
  const [showBulkAssignDropdown, setShowBulkAssignDropdown] = useState(false);
  const [showCreatePostForm, setShowCreatePostForm] = useState(false);
  const [newPostText, setNewPostText] = useState('');
  const [newPostPlatforms, setNewPostPlatforms] = useState<string[]>(['instagram']);
  const [newPostDate, setNewPostDate] = useState('');
  const [newPostFlight, setNewPostFlight] = useState<string | null>(null);
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [showBulkFlightDropdown, setShowBulkFlightDropdown] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [formatFilter, setFormatFilter] = useState<FormatFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [flightFilter, setFlightFilter] = useState<string>('all');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<MediaTab>(() => {
    const saved = sessionStorage.getItem('media_activeTab');
    return (saved as MediaTab) || 'library';
  });
  const [designStatusFilter, setDesignStatusFilter] = useState<DesignStatusFilter>('all');
  const [designCategoryFilter, setDesignCategoryFilter] = useState<string>('all');
  const [savingDesignId, setSavingDesignId] = useState<string | null>(null);

  // Handle saving a design to media library
  const handleSaveDesignToLibrary = async (designId: string) => {
    if (!selectedProjectId) return;

    setSavingDesignId(designId);
    try {
      await saveDesignToLibrary(designId, selectedProjectId);
      // Switch to library tab to show the new media
      setActiveTab('library');
    } catch (err) {
      console.error('Failed to save design:', err);
    } finally {
      setSavingDesignId(null);
    }
  };

  // Persist activeTab to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('media_activeTab', activeTab);
  }, [activeTab]);

  // Check for project query parameter (from project cards link)
  useEffect(() => {
    const projectParam = searchParams.get('project');
    if (projectParam && projects.length > 0) {
      // Verify project exists
      const projectExists = projects.some(p => p.id === projectParam);
      if (projectExists) {
        setSelectedProjectId(projectParam);
      }
    }
  }, [searchParams, projects, setSelectedProjectId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchMedia(selectedProjectId, filter === 'unassigned');
      fetchPosts(selectedProjectId);
      fetchFlights(selectedProjectId);
      fetchSavedDesigns(selectedProjectId);
    }
  }, [selectedProjectId, filter, fetchMedia, fetchPosts, fetchFlights, fetchSavedDesigns]);

  // Auto-select first project if none selected
  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId, setSelectedProjectId]);

  // Helper to get media source type
  const getSourceType = (m: MediaFile): 'upload' | 'design' | 'ai' | 'stock' => {
    if (isAIGenerated(m.url)) return 'ai';
    if (isDesignExport(m.url)) return 'design';
    if (isStockPhoto(m)) return 'stock';
    return 'upload';
  };

  // Helper to get flight name by id
  const getFlightName = (flightId: string | null): string | null => {
    if (!flightId) return null;
    const flight = flights.find(f => f.id === flightId);
    return flight ? flight.name : null;
  };

  // Filtered saved designs
  const filteredDesigns = useMemo(() => {
    return savedDesigns.filter((d) => {
      if (designStatusFilter !== 'all' && d.status !== designStatusFilter) return false;
      if (designCategoryFilter !== 'all' && d.category !== designCategoryFilter) return false;
      return true;
    }).sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [savedDesigns, designStatusFilter, designCategoryFilter]);

  // Grouped designs by status
  const groupedDesigns = useMemo(() => {
    const draft: SavedDesign[] = [];
    const inProgress: SavedDesign[] = [];
    const completed: SavedDesign[] = [];

    filteredDesigns.forEach((d) => {
      if (d.status === 'completed') completed.push(d);
      else if (d.status === 'in_progress') inProgress.push(d);
      else draft.push(d);
    });

    return [
      { key: 'in_progress', label: `${t('designs.inProgress')} (${inProgress.length})`, items: inProgress, color: 'orange' },
      { key: 'completed', label: `${t('designs.completed')} (${completed.length})`, items: completed, color: 'green' },
      { key: 'draft', label: `${t('designs.draft')} (${draft.length})`, items: draft, color: 'gray' },
    ].filter(g => g.items.length > 0);
  }, [filteredDesigns]);

  // Cycle design status
  const handleCycleDesignStatus = async (design: SavedDesign) => {
    const statusOrder: DesignStatus[] = ['draft', 'in_progress', 'completed'];
    const currentIndex = statusOrder.indexOf(design.status || 'draft');
    const nextIndex = (currentIndex + 1) % statusOrder.length;
    const nextStatus = statusOrder[nextIndex];

    try {
      await updateDesign(design.id, { status: nextStatus });
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const filteredMedia = useMemo(() => {
    return media
      .filter((m) => {
        // Assignment filter
        if (filter === 'unassigned' && m.post_id !== null) return false;
        if (filter === 'assigned' && m.post_id === null) return false;
        // Type filter (image/video/audio)
        if (typeFilter !== 'all' && getMediaType(m.mime_type) !== typeFilter) return false;
        // Format filter (square/portrait/story/landscape)
        if (formatFilter !== 'all' && m.format_category !== formatFilter) return false;
        // Source filter (upload/design/ai)
        if (sourceFilter !== 'all' && getSourceType(m) !== sourceFilter) return false;
        // Flight filter
        if (flightFilter !== 'all') {
          if (flightFilter === 'none' && m.flight_id !== null) return false;
          if (flightFilter !== 'none' && m.flight_id !== flightFilter) return false;
        }
        return true;
      })
      .sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'created_at':
            comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            break;
          case 'original_name':
            comparison = a.original_name.localeCompare(b.original_name, 'de');
            break;
          case 'size':
            comparison = a.size - b.size;
            break;
          case 'mime_type':
            comparison = a.mime_type.localeCompare(b.mime_type);
            break;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      });
  }, [media, filter, typeFilter, formatFilter, sourceFilter, flightFilter, sortField, sortOrder]);

  // Group media by type, date, or source
  const groupedMedia = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: t('groupBy.allMedia'), items: filteredMedia }];
    }

    if (groupBy === 'type') {
      const groups: Record<MediaType, MediaFile[]> = { image: [], video: [], audio: [] };
      filteredMedia.forEach((m) => {
        const type = getMediaType(m.mime_type);
        groups[type].push(m);
      });
      return [
        { key: 'image', label: `${t('tabs.images')} (${groups.image.length})`, items: groups.image, icon: '🖼️' },
        { key: 'video', label: `${t('tabs.videos')} (${groups.video.length})`, items: groups.video, icon: '🎬' },
        { key: 'audio', label: `${t('tabs.audio')} (${groups.audio.length})`, items: groups.audio, icon: '🎵' },
      ].filter(g => g.items.length > 0);
    }

    if (groupBy === 'source') {
      const ai: MediaFile[] = [];
      const designs: MediaFile[] = [];
      const stock: MediaFile[] = [];
      const uploadImages: MediaFile[] = [];
      const uploadVideos: MediaFile[] = [];
      const uploadAudio: MediaFile[] = [];

      filteredMedia.forEach((m) => {
        const source = getSourceType(m);
        if (source === 'ai') {
          ai.push(m);
        } else if (source === 'design') {
          designs.push(m);
        } else if (source === 'stock') {
          stock.push(m);
        } else {
          // Split uploads by media type
          const mediaType = getMediaType(m.mime_type);
          if (mediaType === 'image') uploadImages.push(m);
          else if (mediaType === 'video') uploadVideos.push(m);
          else uploadAudio.push(m);
        }
      });

      return [
        { key: 'ai', label: `${t('sources.ai')} (${ai.length})`, items: ai, icon: '🤖' },
        { key: 'design', label: `${t('sources.designCreator')} (${designs.length})`, items: designs, icon: '🎨' },
        { key: 'stock', label: `${t('sources.stock')} (${stock.length})`, items: stock, icon: '📷' },
        { key: 'upload-images', label: `${t('sources.uploadImages')} (${uploadImages.length})`, items: uploadImages, icon: '🖼️' },
        { key: 'upload-videos', label: `${t('sources.uploadVideos')} (${uploadVideos.length})`, items: uploadVideos, icon: '🎬' },
        { key: 'upload-audio', label: `${t('sources.uploadAudio')} (${uploadAudio.length})`, items: uploadAudio, icon: '🎵' },
      ].filter(g => g.items.length > 0);
    }

    if (groupBy === 'format') {
      const groups: Record<string, MediaFile[]> = {
        square: [],
        portrait: [],
        story: [],
        landscape: [],
        unknown_image: [],
        unknown_video: [],
        unknown_audio: [],
      };

      filteredMedia.forEach((m) => {
        const format = m.format_category;
        if (format) {
          groups[format].push(m);
        } else {
          // Split uncategorized by media type
          const mediaType = getMediaType(m.mime_type);
          groups[`unknown_${mediaType}`].push(m);
        }
      });

      return [
        { key: 'story', label: `${t('formats.storyReel')} (${groups.story.length})`, items: groups.story, icon: '📲' },
        { key: 'portrait', label: `${t('formats.fourFive')} (${groups.portrait.length})`, items: groups.portrait, icon: '📱' },
        { key: 'square', label: `${t('formats.square')} (${groups.square.length})`, items: groups.square, icon: '⬜' },
        { key: 'landscape', label: `${t('formats.landscape')} (${groups.landscape.length})`, items: groups.landscape, icon: '🖥️' },
        { key: 'unknown_image', label: `${t('formats.imagesNoFormat')} (${groups.unknown_image.length})`, items: groups.unknown_image, icon: '🖼️' },
        { key: 'unknown_video', label: `${t('formats.videosNoFormat')} (${groups.unknown_video.length})`, items: groups.unknown_video, icon: '🎬' },
        { key: 'unknown_audio', label: `${t('formats.audioSounds')} (${groups.unknown_audio.length})`, items: groups.unknown_audio, icon: '🎵' },
      ].filter(g => g.items.length > 0);
    }

    if (groupBy === 'smart') {
      // Smart grouping: AI -> Designs -> Stock -> Format-kategorisiert -> Rest nach Medientyp
      const ai: MediaFile[] = [];
      const designs: MediaFile[] = [];
      const stock: MediaFile[] = [];
      const story: MediaFile[] = [];
      const portrait: MediaFile[] = [];
      const square: MediaFile[] = [];
      const landscape: MediaFile[] = [];
      const images: MediaFile[] = [];
      const videos: MediaFile[] = [];
      const audio: MediaFile[] = [];

      filteredMedia.forEach((m) => {
        const source = getSourceType(m);
        if (source === 'ai') {
          ai.push(m);
        } else if (source === 'design') {
          designs.push(m);
        } else if (source === 'stock') {
          stock.push(m);
        } else if (m.format_category) {
          // Has format category
          if (m.format_category === 'story') story.push(m);
          else if (m.format_category === 'portrait') portrait.push(m);
          else if (m.format_category === 'square') square.push(m);
          else if (m.format_category === 'landscape') landscape.push(m);
        } else {
          // No format, group by media type
          const mediaType = getMediaType(m.mime_type);
          if (mediaType === 'image') images.push(m);
          else if (mediaType === 'video') videos.push(m);
          else audio.push(m);
        }
      });

      return [
        { key: 'ai', label: `${t('sources.ai')} (${ai.length})`, items: ai, icon: '🤖' },
        { key: 'design', label: `${t('sources.designCreator')} (${designs.length})`, items: designs, icon: '🎨' },
        { key: 'stock', label: `${t('sources.stock')} (${stock.length})`, items: stock, icon: '📷' },
        { key: 'story', label: `${t('formats.storyReel')} (${story.length})`, items: story, icon: '📲' },
        { key: 'portrait', label: `${t('formats.fourFive')} (${portrait.length})`, items: portrait, icon: '📱' },
        { key: 'square', label: `${t('formats.square')} (${square.length})`, items: square, icon: '⬜' },
        { key: 'landscape', label: `${t('formats.landscape')} (${landscape.length})`, items: landscape, icon: '🖥️' },
        { key: 'images', label: `${t('tabs.images')} (${images.length})`, items: images, icon: '🖼️' },
        { key: 'videos', label: `${t('tabs.videos')} (${videos.length})`, items: videos, icon: '🎬' },
        { key: 'audio', label: `${t('formats.audioSounds')} (${audio.length})`, items: audio, icon: '🎵' },
      ].filter(g => g.items.length > 0);
    }

    if (groupBy === 'date') {
      const groups: Record<string, MediaFile[]> = {};
      filteredMedia.forEach((m) => {
        const date = new Date(m.created_at).toLocaleDateString('de-DE', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
        if (!groups[date]) groups[date] = [];
        groups[date].push(m);
      });
      return Object.entries(groups).map(([date, items]) => ({
        key: date,
        label: `${date} (${items.length})`,
        items,
      }));
    }

    return [{ key: 'all', label: t('groupBy.allMedia'), items: filteredMedia }];
  }, [filteredMedia, groupBy, t]);

  // Count by type for the type filter badges
  const typeCounts = useMemo(() => {
    const counts = { all: 0, image: 0, video: 0, audio: 0 };
    media
      .filter((m) => {
        if (filter === 'unassigned' && m.post_id !== null) return false;
        if (filter === 'assigned' && m.post_id === null) return false;
        return true;
      })
      .forEach((m) => {
        counts.all++;
        counts[getMediaType(m.mime_type)]++;
      });
    return counts;
  }, [media, filter]);

  const toggleGroupCollapse = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleUpload = (_media: MediaFile) => {
    // Media is already added to store by MediaUpload component
  };

  const handleDelete = async (mediaId: string) => {
    if (window.confirm(t('deleteConfirm'))) {
      try {
        await deleteMedia(mediaId);
        setSelectedMedia((prev) => {
          const next = new Set(prev);
          next.delete(mediaId);
          return next;
        });
      } catch {
        // Error is handled by store
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMedia.size === 0) return;
    if (!window.confirm(t('deleteConfirmBulk', { count: selectedMedia.size }))) return;

    for (const id of selectedMedia) {
      try {
        await deleteMedia(id);
      } catch {
        // Continue with next
      }
    }
    setSelectedMedia(new Set());
  };

  const handleBulkFlightAssign = async (flightId: string | null) => {
    if (selectedMedia.size === 0) return;
    try {
      await batchAssignToFlight(Array.from(selectedMedia), flightId);
      setShowBulkFlightDropdown(false);
      setSelectedMedia(new Set());
    } catch {
      // Error handled by store
    }
  };

  const toggleSelection = (mediaId: string) => {
    setSelectedMedia((prev) => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedMedia.size === filteredMedia.length) {
      setSelectedMedia(new Set());
    } else {
      setSelectedMedia(new Set(filteredMedia.map((m) => m.id)));
    }
  };

  // Select/deselect all media in a group
  const toggleGroupSelection = (groupItems: MediaFile[]) => {
    const groupIds = new Set(groupItems.map(m => m.id));
    const allSelected = groupItems.every(m => selectedMedia.has(m.id));

    setSelectedMedia(prev => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all in group
        groupIds.forEach(id => next.delete(id));
      } else {
        // Select all in group
        groupIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Check if all items in a group are selected
  const isGroupSelected = (groupItems: MediaFile[]) => {
    return groupItems.length > 0 && groupItems.every(m => selectedMedia.has(m.id));
  };

  // Check if some (but not all) items in a group are selected
  const isGroupPartiallySelected = (groupItems: MediaFile[]) => {
    const selectedCount = groupItems.filter(m => selectedMedia.has(m.id)).length;
    return selectedCount > 0 && selectedCount < groupItems.length;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isImage = (mimeType: string) => mimeType.startsWith('image/');
  const isVideo = (mimeType: string) => mimeType.startsWith('video/');
  const isAudio = (mimeType: string) => mimeType.startsWith('audio/');

  // Get editable posts (draft or scheduled)
  const editablePosts = posts.filter((p) => ['draft', 'scheduled'].includes(p.status));

  // Filter posts by search query (text, platforms, or display_id)
  const filteredEditablePosts = postSearchQuery.trim()
    ? editablePosts.filter((post) =>
        post.text.toLowerCase().includes(postSearchQuery.toLowerCase()) ||
        post.platforms.some((p) => p.toLowerCase().includes(postSearchQuery.toLowerCase())) ||
        (post.display_id && post.display_id.toLowerCase().includes(postSearchQuery.toLowerCase()))
      )
    : editablePosts;

  const handleOpenAssignModal = (mediaFile: MediaFile) => {
    setMediaToAssign(mediaFile);
    setShowAssignModal(true);
    setPostSearchQuery('');
  };

  const handleAssignToPost = async (post: Post) => {
    if (!mediaToAssign) return;

    // Add media URL to post's existing media_urls
    const newMediaUrls = [...(post.media_urls || []), mediaToAssign.url];

    try {
      await updatePost(post.id, { media_urls: newMediaUrls });
      setShowAssignModal(false);
      setMediaToAssign(null);
      // Refresh media to update post_id status
      if (selectedProjectId) {
        fetchMedia(selectedProjectId, filter === 'unassigned');
      }
    } catch {
      // Error is handled by store
    }
  };

  const handleCreateNewPost = async () => {
    if (!mediaToAssign || !selectedProjectId) return;
    // Text is optional when media is already attached
    if (newPostPlatforms.length === 0 || !newPostDate) return;

    setIsCreatingPost(true);
    try {
      await createPost({
        project_id: selectedProjectId,
        text: newPostText.trim(), // Can be empty if media is attached
        media_urls: [mediaToAssign.url],
        platforms: newPostPlatforms,
        scheduled_for: new Date(newPostDate).toISOString(),
        status: 'scheduled',
        flight_id: newPostFlight,
      });

      // Reset form and close modal
      setShowCreatePostForm(false);
      setNewPostText('');
      setNewPostPlatforms(['instagram']);
      setNewPostDate('');
      setNewPostFlight(null);
      setShowAssignModal(false);
      setMediaToAssign(null);

      // Refresh media to update post_id status
      if (selectedProjectId) {
        fetchMedia(selectedProjectId, filter === 'unassigned');
        fetchPosts(selectedProjectId);
      }
    } catch {
      // Error is handled by store
    } finally {
      setIsCreatingPost(false);
    }
  };

  const resetCreatePostForm = () => {
    setShowCreatePostForm(false);
    setNewPostText('');
    setNewPostPlatforms(['instagram']);
    setNewPostDate('');
    setNewPostFlight(null);
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setMediaToAssign(null);
    resetCreatePostForm();
  };

  const handleBulkAssignToPost = async (post: Post) => {
    if (selectedMedia.size === 0) return;

    const selectedMediaFiles = media.filter((m) => selectedMedia.has(m.id));
    const newMediaUrls = [
      ...(post.media_urls || []),
      ...selectedMediaFiles.map((m) => m.url),
    ];

    try {
      await updatePost(post.id, { media_urls: newMediaUrls });
      setSelectedMedia(new Set());
      // Refresh media
      if (selectedProjectId) {
        fetchMedia(selectedProjectId, filter === 'unassigned');
      }
    } catch {
      // Error is handled by store
    }
  };

  const handleOpenCropModal = (mediaFile: MediaFile) => {
    setMediaToCrop(mediaFile);
    setShowCropModal(true);
  };

  const handleCropSave = async (cropArea: CropData, format: FormatCategory) => {
    if (!mediaToCrop) return;

    try {
      const result = await cropMedia(mediaToCrop.id, cropArea, {
        id: format.id,
        aspectRatio: format.aspectRatio,
      });

      // Show success message
      alert(
        `${t('cropSuccess')}\n\n` +
        `${t('cropFormat', { format: format.nameDe, ratio: format.aspectRatio })}\n` +
        `${t('cropOutput', { width: result.cropInfo.outputWidth, height: result.cropInfo.outputHeight })}\n` +
        `${t('cropNewFile', { filename: result.croppedMedia.original_name })}`
      );

      setShowCropModal(false);
      setMediaToCrop(null);
    } catch (err) {
      // Error is handled by the store and shown in the error alert
      console.error('Crop failed:', err);
    }
  };

  // Handle stock photo download
  const handleStockDownload = (_mediaId: string, _url: string) => {
    // Refresh media list in background (for when user switches to library tab later)
    if (selectedProjectId) {
      fetchMedia(selectedProjectId, filter === 'unassigned');
    }
    // Bleibe im Stockfotos-Tab - StockPhotoSearch zeigt bereits eine Success-Message
  };

  // Get selected project name for breadcrumb
  const selectedProject = projects.find(p => p.id === selectedProjectId);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        icon={
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        }
        title={t('pageTitle')}
        breadcrumbs={selectedProject ? [
          { label: t('common:projects', 'Projekte'), href: '/projects' },
          { label: selectedProject.name, href: `/projects/${selectedProject.id}` },
        ] : [{ label: t('common:dashboard', 'Dashboard'), href: '/' }]}
      />

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('library')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'library'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('tabs.library')}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('stock')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'stock'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {t('tabs.stock')}
              <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">{t('common:free', 'Kostenlos')}</span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('designs')}
            className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'designs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              {t('tabs.designs')}
              {savedDesigns.length > 0 && (
                <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{savedDesigns.length}</span>
              )}
            </span>
          </button>
        </nav>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          {error}
          <button
            onClick={clearError}
            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Stock Photos Tab Content */}
      {activeTab === 'stock' && (
        <div className="bg-white rounded-lg shadow p-6">
          {!selectedProjectId ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-gray-500 font-medium">{t('selectProject')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('selectProjectHint')}</p>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
                className="mt-4 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('chooseProject')}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{t('stock.title')}</h2>
                  <p className="text-sm text-gray-500">
                    {t('stock.subtitle')}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>{t('project')}:</span>
                  <select
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value || null)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <StockPhotoSearch
                projectId={selectedProjectId}
                onDownload={handleStockDownload}
              />
            </>
          )}
        </div>
      )}

      {/* Designs Tab Content */}
      {activeTab === 'designs' && (
        <div className="bg-white rounded-lg shadow p-6">
          {!selectedProjectId ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              <p className="text-gray-500 font-medium">{t('selectProject')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('designs.subtitle')}</p>
              <select
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
                className="mt-4 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('chooseProject')}</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              {/* Designs Header & Filters */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{t('designs.title')}</h2>
                  <p className="text-sm text-gray-500">
                    {t('designs.subtitle')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Status Filter */}
                  <select
                    value={designStatusFilter}
                    onChange={(e) => setDesignStatusFilter(e.target.value as DesignStatusFilter)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">{t('designs.allStatus')}</option>
                    <option value="in_progress">{t('designs.inProgress')}</option>
                    <option value="completed">{t('designs.completed')}</option>
                    <option value="draft">{t('designs.draft')}</option>
                  </select>
                  {/* Category Filter */}
                  <select
                    value={designCategoryFilter}
                    onChange={(e) => setDesignCategoryFilter(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">{t('designs.allFormats')}</option>
                    <option value="post">Post</option>
                    <option value="story">Story</option>
                    <option value="reel">Reel</option>
                    <option value="carousel">Carousel</option>
                  </select>
                  {/* Project Selector */}
                  <select
                    value={selectedProjectId}
                    onChange={(e) => setSelectedProjectId(e.target.value || null)}
                    className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                  >
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                  {/* Open Creator Studio */}
                  <button
                    onClick={() => navigate(`/creator?project=${selectedProjectId}`)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('designs.newDesign')}
                  </button>
                </div>
              </div>

              {/* Loading */}
              {isLoadingDesigns && (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                  <p className="text-gray-500 mt-2">{t('designs.loading')}</p>
                </div>
              )}

              {/* Empty State */}
              {!isLoadingDesigns && savedDesigns.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">{t('designs.empty')}</h3>
                  <p className="text-gray-500 mb-4">{t('designs.emptyHint')}</p>
                  <button
                    onClick={() => navigate(`/creator?project=${selectedProjectId}`)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('designs.openCreator')}
                  </button>
                </div>
              )}

              {/* Designs Grid grouped by status */}
              {!isLoadingDesigns && savedDesigns.length > 0 && (
                <div className="space-y-8">
                  {groupedDesigns.map((group) => (
                    <div key={group.key}>
                      <div className="flex items-center gap-2 mb-4">
                        <span className={`w-3 h-3 rounded-full ${
                          group.color === 'orange' ? 'bg-orange-500' :
                          group.color === 'green' ? 'bg-green-500' : 'bg-gray-400'
                        }`}></span>
                        <h3 className="text-sm font-semibold text-gray-700">{group.label}</h3>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {group.items.map((design) => (
                          <div
                            key={design.id}
                            className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
                          >
                            {/* Thumbnail */}
                            <div className="aspect-square bg-gray-100 relative">
                              {design.thumbnail_url ? (
                                <img
                                  src={getMediaUrl(design.thumbnail_url)}
                                  alt={design.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}
                              {/* Status Badge */}
                              <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                                design.status === 'completed' ? 'bg-green-100 text-green-700' :
                                design.status === 'in_progress' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {t(`designs.statusLabel.${design.status || 'draft'}`)}
                              </div>
                              {/* Category Badge */}
                              <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white rounded text-xs">
                                {design.category}
                              </div>
                            </div>
                            {/* Info */}
                            <div className="p-3">
                              <h4 className="font-medium text-sm text-gray-900 truncate">{design.name}</h4>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(design.updated_at).toLocaleDateString('de-DE')}
                              </p>
                            </div>
                            {/* Hover Actions */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              {/* Save to Library Button */}
                              <button
                                onClick={() => handleSaveDesignToLibrary(design.id)}
                                disabled={savingDesignId === design.id}
                                className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={t('media:designs.saveToMediaLibrary')}
                              >
                                {savingDesignId === design.id ? (
                                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={() => navigate(`/creator?project=${selectedProjectId}&design=${design.id}`)}
                                className="px-3 py-1.5 bg-white text-gray-900 text-sm font-medium rounded-md hover:bg-gray-100"
                                title={t('media:designs.openInCreator')}
                              >
                                {t('common:edit')}
                              </button>
                              <button
                                onClick={() => handleCycleDesignStatus(design)}
                                className={`p-2 text-white rounded-md ${
                                  design.status === 'completed' ? 'bg-green-600 hover:bg-green-700' :
                                  design.status === 'in_progress' ? 'bg-orange-500 hover:bg-orange-600' :
                                  'bg-gray-500 hover:bg-gray-600'
                                }`}
                                title={
                                  design.status === 'completed' ? t('creator:statusCompletedToDraft') :
                                  design.status === 'in_progress' ? t('creator:statusInProgressToCompleted') :
                                  t('creator:statusDraftToInProgress')
                                }
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Library Tab Content */}
      {activeTab === 'library' && (
        <>
          {/* Toolbar */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              {/* Project Selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('media:project')}</label>
            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(e.target.value || null)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('media:chooseProject')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('media:filters.filter')}</label>
            <div className="flex gap-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-2 text-sm rounded-l-md border ${
                  filter === 'all'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('media:filters.all')}
              </button>
              <button
                onClick={() => setFilter('unassigned')}
                className={`px-3 py-2 text-sm border-t border-b ${
                  filter === 'unassigned'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('media:filters.unassigned')}
              </button>
              <button
                onClick={() => setFilter('assigned')}
                className={`px-3 py-2 text-sm rounded-r-md border ${
                  filter === 'assigned'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('media:filters.assigned')}
              </button>
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('media:tabs.library')}</label>
            <div className="flex gap-1">
              <button
                onClick={() => setTypeFilter('all')}
                className={`px-3 py-2 text-sm rounded-l-md border ${
                  typeFilter === 'all'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('media:filters.all')} ({typeCounts.all})
              </button>
              <button
                onClick={() => setTypeFilter('image')}
                className={`px-3 py-2 text-sm border-t border-b ${
                  typeFilter === 'image'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                title={t('media:tabs.images')}
              >
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {typeCounts.image}
                </span>
              </button>
              <button
                onClick={() => setTypeFilter('video')}
                className={`px-3 py-2 text-sm border-t border-b border-l-0 ${
                  typeFilter === 'video'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                title={t('media:tabs.videos')}
              >
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {typeCounts.video}
                </span>
              </button>
              <button
                onClick={() => setTypeFilter('audio')}
                className={`px-3 py-2 text-sm rounded-r-md border ${
                  typeFilter === 'audio'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                title={t('media:tabs.audio')}
              >
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  {typeCounts.audio}
                </span>
              </button>
            </div>
          </div>

          {/* Grouping */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('media:groupBy.smart')}</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="none">{t('media:groupBy.none')}</option>
              <option value="smart">✨ {t('media:groupBy.smart')}</option>
              <option value="format">📐 {t('media:groupBy.format')}</option>
              <option value="type">🖼️ {t('media:groupBy.type')}</option>
              <option value="source">📁 {t('media:groupBy.source')}</option>
              <option value="date">📅 {t('media:groupBy.date')}</option>
            </select>
          </div>

          {/* Format Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
            <select
              value={formatFilter || 'all'}
              onChange={(e) => setFormatFilter(e.target.value === 'all' ? 'all' : e.target.value as FormatCategoryType)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle Formate</option>
              <option value="story">📲 Story/Reel (9:16)</option>
              <option value="portrait">📱 Hochformat (4:5)</option>
              <option value="square">⬜ Quadrat (1:1)</option>
              <option value="landscape">🖥️ Querformat (16:9)</option>
            </select>
          </div>

          {/* Source Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quelle</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Alle Quellen</option>
              <option value="ai">KI-generiert</option>
              <option value="design">Creator-Designs</option>
              <option value="stock">Stockfotos</option>
              <option value="upload">Uploads</option>
            </select>
          </div>

          {/* Flight Filter */}
          {flights.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flight</label>
              <select
                value={flightFilter}
                onChange={(e) => setFlightFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Alle Flights</option>
                <option value="none">Ohne Flight</option>
                {flights.map((flight) => (
                  <option key={flight.id} value={flight.id}>
                    {flight.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:filter')}</label>
            <div className="flex gap-1">
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value as SortField)}
                className="border border-gray-300 rounded-l-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="created_at">Datum</option>
                <option value="original_name">Name</option>
                <option value="size">{t('media:sort.size')}</option>
                <option value="mime_type">Typ</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-2 py-2 border border-gray-300 rounded-r-md bg-white hover:bg-gray-50"
                title={sortOrder === 'asc' ? t('media:sort.ascending') : t('media:sort.descending')}
              >
                {sortOrder === 'asc' ? (
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* View Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('media:view.mode')}</label>
            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-l-md border ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-r-md border ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedMedia.size > 0 && (
            <div className="flex items-end gap-2">
              {/* Assign to Post Dropdown with Search */}
              {editablePosts.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowBulkAssignDropdown(!showBulkAssignDropdown);
                      setPostSearchQuery('');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    {t('media:bulk.addToPost')}
                    <svg className={`w-4 h-4 transition-transform ${showBulkAssignDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showBulkAssignDropdown && (
                    <>
                      {/* Backdrop to close dropdown */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowBulkAssignDropdown(false)}
                      />
                      <div className="absolute right-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                        {/* Search Input */}
                        <div className="p-2 border-b">
                          <div className="relative">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                              type="text"
                              placeholder={t('media:bulk.searchPosts')}
                              value={postSearchQuery}
                              onChange={(e) => setPostSearchQuery(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                          {filteredEditablePosts.length === 0 ? (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                              {postSearchQuery ? t('media:bulk.noPosts') : t('media:bulk.noEditablePosts')}
                            </div>
                          ) : (
                            filteredEditablePosts.map((post) => (
                              <button
                                key={post.id}
                                onClick={() => {
                                  handleBulkAssignToPost(post);
                                  setShowBulkAssignDropdown(false);
                                  setPostSearchQuery('');
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
                              >
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${post.status === 'draft' ? 'bg-gray-400' : 'bg-blue-500'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-mono text-gray-400">{post.display_id}</span>
                                    <p className="text-sm font-medium text-gray-900 truncate">{post.text.slice(0, 35)}{post.text.length > 35 ? '...' : ''}</p>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {post.status === 'draft' ? t('media:status.draft') : t('media:status.scheduled')} · {post.platforms.join(', ')}
                                  </p>
                                </div>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Batch Flight Assignment */}
              {flights.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowBulkFlightDropdown(!showBulkFlightDropdown)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Flight zuweisen
                    <svg className={`w-4 h-4 transition-transform ${showBulkFlightDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showBulkFlightDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border z-50">
                      <div className="p-2">
                        <button
                          onClick={() => handleBulkFlightAssign(null)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-100 rounded text-sm text-gray-600"
                        >
                          Keine Zuordnung
                        </button>
                        {flights.map((flight) => (
                          <button
                            key={flight.id}
                            onClick={() => handleBulkFlightAssign(flight.id)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 rounded flex items-center gap-2"
                          >
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              flight.status === 'active' ? 'bg-green-500' :
                              flight.status === 'draft' ? 'bg-gray-400' : 'bg-blue-500'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{flight.name}</p>
                              <p className="text-xs text-gray-500">{flight.display_id}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                {t('media:bulk.deleteSelected', { count: selectedMedia.size })}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Upload Section */}
      {selectedProjectId && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-3">{t('media:uploadSection.title')}</h2>
          <MediaUpload projectId={selectedProjectId} onUpload={handleUpload} />
        </div>
      )}

      {/* Media Grid/List */}
      {!selectedProjectId ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          {t('media:noProject')}
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          {t('common:loading')}
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          {t('media:noMedia')}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="space-y-6">
          {/* Select All */}
          <div className="bg-white rounded-lg shadow p-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedMedia.size === filteredMedia.length && filteredMedia.length > 0}
                onChange={selectAll}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">
                {t('media:selectAllCount', { count: filteredMedia.length })}
              </span>
            </label>
          </div>

          {/* Grouped Grid */}
          {groupedMedia.map((group) => (
            <div key={group.key} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Group Header (only show if grouping is enabled) */}
              {groupBy !== 'none' && (
                <div className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    {/* Group Selection Checkbox */}
                    <input
                      type="checkbox"
                      checked={isGroupSelected(group.items)}
                      ref={(el) => {
                        if (el) el.indeterminate = isGroupPartiallySelected(group.items);
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleGroupSelection(group.items);
                      }}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      title={t('media:selectGroupAll', { count: group.items.length })}
                    />
                    <button
                      onClick={() => toggleGroupCollapse(group.key)}
                      className="flex items-center gap-2 flex-1"
                    >
                      {'icon' in group && <span className="text-lg">{group.icon}</span>}
                      <span className="font-medium text-gray-900">{group.label}</span>
                    </button>
                  </div>
                  <button
                    onClick={() => toggleGroupCollapse(group.key)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${collapsedGroups.has(group.key) ? '' : 'rotate-180'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Grid Content */}
              {!collapsedGroups.has(group.key) && (
                <div className="p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {group.items.map((m) => (
                      <div
                        key={m.id}
                        className={`relative group rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedMedia.has(m.id) ? 'border-blue-500' : 'border-transparent'
                        }`}
                      >
                        {/* Selection Checkbox */}
                        <div className="absolute top-2 left-2 z-10">
                          <input
                            type="checkbox"
                            checked={selectedMedia.has(m.id)}
                            onChange={() => toggleSelection(m.id)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 bg-white"
                          />
                        </div>

                        {/* Thumbnail */}
                        <div
                          className="aspect-square bg-gray-100 cursor-pointer"
                          onClick={() => setPreviewMedia(m)}
                        >
                          {isImage(m.mime_type) ? (
                            <img src={m.url} alt={m.original_name} className="w-full h-full object-cover" />
                          ) : isVideo(m.mime_type) ? (
                            <div className="w-full h-full flex items-center justify-center bg-gray-900">
                              <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          ) : isAudio(m.mime_type) ? (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                              </svg>
                              <span className="text-white text-xs mt-1 font-medium">Audio</span>
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Status Badges */}
                        <div className="absolute top-2 right-2 flex flex-col gap-1">
                          {isStockPhoto(m) && (
                            <div className={`text-white text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                              m.stock_source === 'unsplash' ? 'bg-black' : 'bg-teal-500'
                            }`}>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {m.stock_source === 'unsplash' ? 'Unsplash' : 'Pexels'}
                            </div>
                          )}
                          {isDesignExport(m.url) && (
                            <div className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                              </svg>
                              Design
                            </div>
                          )}
                          {(m.post_ids?.length > 0 || m.post_id) && (
                            <div className="bg-green-500 text-white text-xs px-2 py-0.5 rounded">
                              {m.post_ids?.length > 1
                                ? `${m.post_ids.length} Posts`
                                : t('media:fileInfo.assigned')}
                            </div>
                          )}
                          {m.flight_id && getFlightName(m.flight_id) && (
                            <div className="bg-indigo-500 text-white text-xs px-2 py-0.5 rounded flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                              {getFlightName(m.flight_id)}
                            </div>
                          )}
                        </div>

                        {/* Overlay with actions */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          {/* Download button */}
                          <a
                            href={m.url}
                            download={m.original_name}
                            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700"
                            title={t('common:download')}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                          {/* Crop button (only for images) */}
                          {isImage(m.mime_type) && (
                            <button
                              onClick={() => handleOpenCropModal(m)}
                              className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700"
                              title={t('media:actions.crop')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                              </svg>
                            </button>
                          )}
                          {editablePosts.length > 0 && (
                            <button
                              onClick={() => handleOpenAssignModal(m)}
                              className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700"
                              title={t('media:actions.assignToPost')}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(m.id)}
                            className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                            title={t('common:delete')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>

                        {/* File Info */}
                        <div className="p-2 bg-white">
                          <p className="text-xs text-gray-900 truncate" title={m.original_name}>
                            {m.original_name}
                          </p>
                          <p className="text-xs text-gray-500">{formatFileSize(m.size)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedMedia.size === filteredMedia.length && filteredMedia.length > 0}
                    onChange={selectAll}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vorschau
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('original_name')}
                >
                  <div className="flex items-center gap-1">
                    Dateiname
                    {sortField === 'original_name' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('mime_type')}
                >
                  <div className="flex items-center gap-1">
                    Typ
                    {sortField === 'mime_type' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('size')}
                >
                  <div className="flex items-center gap-1">
                    {t('media:sort.size')}
                    {sortField === 'size' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    {t('media:fileInfo.uploaded')}
                    {sortField === 'created_at' && (
                      <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMedia.map((m) => (
                <tr
                  key={m.id}
                  className={`hover:bg-gray-50 ${selectedMedia.has(m.id) ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedMedia.has(m.id)}
                      onChange={() => toggleSelection(m.id)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className="w-12 h-12 rounded overflow-hidden cursor-pointer"
                      onClick={() => setPreviewMedia(m)}
                    >
                      {isImage(m.mime_type) ? (
                        <img src={m.url} alt="" className="w-full h-full object-cover" />
                      ) : isVideo(m.mime_type) ? (
                        <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      ) : isAudio(m.mime_type) ? (
                        <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900">{m.original_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">{m.mime_type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">{formatFileSize(m.size)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {m.post_id ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('media:fileInfo.assigned')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {t('media:fileInfo.available')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-500">{formatDate(m.created_at)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <a
                        href={m.url}
                        download={m.original_name}
                        className="text-green-600 hover:text-green-800"
                        title={t('common:download')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </a>
                      <button
                        onClick={() => handleDelete(m.id)}
                        className="text-red-600 hover:text-red-800"
                        title={t('common:delete')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      {previewMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h3 className="font-medium text-gray-900">{previewMedia.original_name}</h3>
                <p className="text-sm text-gray-500">
                  {formatFileSize(previewMedia.size)} · {previewMedia.mime_type}
                </p>
              </div>
              <button
                onClick={() => setPreviewMedia(null)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Preview Content */}
            <div className="p-4 max-h-[70vh] overflow-auto">
              {isImage(previewMedia.mime_type) ? (
                <img
                  src={previewMedia.url}
                  alt={previewMedia.original_name}
                  className="max-w-full h-auto mx-auto"
                />
              ) : isVideo(previewMedia.mime_type) ? (
                <video src={previewMedia.url} controls className="max-w-full h-auto mx-auto">
                  {t('media:preview.videoNotSupported')}
                </video>
              ) : isAudio(previewMedia.mime_type) ? (
                <div className="flex flex-col items-center py-8">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-6">
                    <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                  <audio src={previewMedia.url} controls className="w-full max-w-md">
                    {t('media:preview.audioNotSupported')}
                  </audio>
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  {t('media:preview.notAvailable')}
                </div>
              )}
            </div>

            {/* Preview Footer */}
            <div className="flex justify-between items-center p-4 border-t bg-gray-50">
              <div className="text-sm text-gray-500">
                {t('media:fileInfo.uploaded')}: {formatDate(previewMedia.created_at)}
              </div>
              <div className="flex gap-2">
                <a
                  href={previewMedia.url}
                  download={previewMedia.original_name}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {t('common:download')}
                </a>
                <button
                  onClick={() => {
                    handleDelete(previewMedia.id);
                    setPreviewMedia(null);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {t('common:delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Crop Editor Modal */}
      {mediaToCrop && (
        <CropEditorModal
          isOpen={showCropModal}
          onClose={() => {
            if (!isCropping) {
              setShowCropModal(false);
              setMediaToCrop(null);
            }
          }}
          imageUrl={mediaToCrop.url}
          onSave={handleCropSave}
          isSaving={isCropping}
        />
      )}

      {/* Assign to Post Modal */}
      {showAssignModal && mediaToAssign && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeAssignModal}
        >
          <div
            className="bg-white rounded-lg w-full max-w-md max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{showCreatePostForm ? t('media:assignModal.createNewPost') : t('media:assignModal.addToPost')}</h3>
              <button
                onClick={closeAssignModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Media Preview */}
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                {isImage(mediaToAssign.mime_type) ? (
                  <img
                    src={mediaToAssign.url}
                    alt={mediaToAssign.original_name}
                    className="w-16 h-16 object-cover rounded"
                  />
                ) : isVideo(mediaToAssign.mime_type) ? (
                  <div className="w-16 h-16 bg-gray-900 rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                ) : isAudio(mediaToAssign.mime_type) ? (
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{mediaToAssign.original_name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(mediaToAssign.size)}</p>
                </div>
              </div>
            </div>

            {/* Create New Post Form */}
            {showCreatePostForm ? (
              <div className="p-4">
                <div className="space-y-4">
                  {/* Post Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('media:assignModal.postText')} <span className="text-gray-400 font-normal">({t('media:assignModal.optional')})</span></label>
                    <textarea
                      value={newPostText}
                      onChange={(e) => setNewPostText(e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('media:assignModal.captionPlaceholder')}
                    />
                  </div>

                  {/* Platforms */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('media:assignModal.platforms')} *</label>
                    <div className="flex gap-2">
                      {['instagram', 'facebook'].map((platform) => (
                        <button
                          key={platform}
                          type="button"
                          onClick={() => {
                            setNewPostPlatforms((prev) =>
                              prev.includes(platform)
                                ? prev.filter((p) => p !== platform)
                                : [...prev, platform]
                            );
                          }}
                          className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                            newPostPlatforms.includes(platform)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {platform === 'instagram' ? 'Instagram' : 'Facebook'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Datum & Uhrzeit *</label>
                    <input
                      type="datetime-local"
                      value={newPostDate}
                      onChange={(e) => setNewPostDate(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Flight */}
                  {flights.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Flight (optional)</label>
                      <select
                        value={newPostFlight || ''}
                        onChange={(e) => setNewPostFlight(e.target.value || null)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Kein Flight</option>
                        {flights.map((flight) => (
                          <option key={flight.id} value={flight.id}>{flight.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                  <button
                    onClick={resetCreatePostForm}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
                    disabled={isCreatingPost}
                  >
                    {t('common:cancel')}
                  </button>
                  <button
                    onClick={handleCreateNewPost}
                    disabled={isCreatingPost || newPostPlatforms.length === 0 || !newPostDate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isCreatingPost ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('media:assignModal.creating')}
                      </>
                    ) : (
                      t('media:assignModal.create')
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Create New Post Button */}
                <div className="p-3 border-b">
                  <button
                    onClick={() => setShowCreatePostForm(true)}
                    className="w-full p-3 text-left rounded-lg border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center gap-3"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-blue-700">{t('media:assignModal.createNewPost')}</p>
                      <p className="text-xs text-gray-500">{t('media:assignModal.withThisMedia')}</p>
                    </div>
                  </button>
                </div>

                {/* Search Input */}
                <div className="p-3 border-b">
                  <p className="text-xs text-gray-500 mb-2">{t('media:assignModal.orAddToExisting')}</p>
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder={t('media:bulk.searchPosts')}
                      value={postSearchQuery}
                      onChange={(e) => setPostSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Post List */}
                <div className="p-4 max-h-60 overflow-y-auto">
                  {filteredEditablePosts.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">
                      {postSearchQuery ? (
                        <>{t('media:assignModal.noPostsFound', { query: postSearchQuery })}</>
                      ) : (
                        <>
                          {t('media:assignModal.noEditablePosts')}<br />
                          <span className="text-sm">{t('media:assignModal.createNewHint')}</span>
                        </>
                      )}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {filteredEditablePosts.map((post) => (
                        <button
                          key={post.id}
                          onClick={() => handleAssignToPost(post)}
                          className="w-full p-3 text-left rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                        >
                          <div className="flex items-start gap-2">
                            <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${post.status === 'draft' ? 'bg-gray-400' : 'bg-blue-500'}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{post.display_id}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${post.status === 'draft' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                                  {post.status === 'draft' ? t('media:status.draft') : t('media:status.scheduled')}
                                </span>
                              </div>
                              <p className="font-medium text-gray-900 line-clamp-2">{post.text}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                <span>{post.platforms.join(', ')}</span>
                                {post.media_urls.length > 0 && (
                                  <>
                                    <span>·</span>
                                    <span>{t('media:assignModal.mediaCount', { count: post.media_urls.length })}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end p-4 border-t bg-gray-50">
                  <button
                    onClick={closeAssignModal}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
